import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "@/lib/apiClient";
import { MapPin, CloudRain, ShieldAlert, Sun } from "lucide-react";

const RAIN = {
  heavy: { label: "Heavy rain", cls: "bg-blue-700 text-white" },
  moderate: { label: "Rain likely", cls: "bg-blue-100 text-blue-800" },
  none: { label: "Dry", cls: "bg-stone-100 text-stone-600" },
};
const RISK = { high: "bg-red-600 text-white", medium: "bg-amber-500 text-white", low: "bg-emerald-600 text-white" };

export function FieldStatusBoard({ fields }) {
  const navigate = useNavigate();
  const [rain, setRain] = useState({});
  const pinned = fields.filter((f) => typeof f.lat === "number" && typeof f.lon === "number");

  useEffect(() => {
    if (!pinned.length) return;
    apiClient.get("/fields/rain-alerts").then((r) => setRain(Object.fromEntries(r.data.map((a) => [a.field_id, a])))).catch(() => {});
  }, [pinned.length]);

  if (!pinned.length) return null;

  return (
    <div className="mb-8" data-testid="field-status-board">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl font-bold text-emerald-900 flex items-center gap-2"><MapPin className="h-6 w-6 text-[#B91C1C]" /> Field status</h2>
        <button onClick={() => navigate("/app/map")} className="text-sm font-semibold text-[#2D6A4F] hover:underline">Open map</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {pinned.map((f) => {
          const r = rain[f.id];
          const rl = RAIN[r?.level || "none"];
          const risk = f.pest_risk?.overall_risk;
          const riskCls = RISK[(risk || "").toLowerCase()];
          return (
            <button key={f.id} data-testid={`status-card-${f.id}`} onClick={() => navigate("/app/map")}
              className="text-left bg-white rounded-2xl border border-stone-200 p-5 hover:border-[#52B788] hover:shadow-lg transition-all">
              <div className="flex items-center justify-between gap-2 mb-3">
                <span className="font-display font-bold text-emerald-950 truncate">{f.name}</span>
                <span className="font-mono-tech text-xs text-stone-500 shrink-0">Score {Math.round(f.analysis?.soil_health?.score || 0)}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <span data-testid={`status-rain-${f.id}`} className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${rl.cls}`}>
                  {r?.level && r.level !== "none" ? <CloudRain className="h-3 w-3" /> : <Sun className="h-3 w-3" />} {r ? rl.label : "Checking…"}{r && r.level !== "none" ? ` · ${r.total_mm} mm` : ""}
                </span>
                <span data-testid={`status-risk-${f.id}`} className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${riskCls || "bg-stone-100 text-stone-500"}`}>
                  <ShieldAlert className="h-3 w-3" /> {risk ? `${risk} pest risk` : "Pest risk not checked"}
                </span>
              </div>
              {f.pest_risk?.warnings?.[0] && <p className="text-xs text-stone-600 mt-3 line-clamp-2"><b>Watch:</b> {f.pest_risk.warnings.slice(0, 2).map((w) => w.problem).join(", ")}</p>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
